import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneCall, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { StatutBadge, STATUT_APPEL, LABELS_MAP } from "@/components/StatusBadge";

const TYPES = ["entrant", "sortant", "manque"];
const MOTIFS = ["information", "reclamation", "rendez_vous", "suivi_dossier", "orientation", "autre"];

const ICONS = {
  entrant: PhoneIncoming,
  sortant: PhoneOutgoing,
  manque: PhoneMissed,
};

export default function Appels() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: "entrant", motif: "information", numero: "", duree_sec: 0, client_id: "",
    objet: "", resume: "", notes: "", genere_reclamation: false,
  });

  const load = async () => {
    const params = { limit: 300 };
    if (typeFilter !== "all") params.type = typeFilter;
    const { data } = await api.get("/appels", { params });
    setRows(data);
  };

  useEffect(() => { load(); }, [typeFilter]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => { /* ignore */ });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, duree_sec: Number(form.duree_sec) || 0 };
      if (!payload.client_id) delete payload.client_id;
      if (editing) {
        await api.patch(`/appels/${editing.id}`, payload);
        toast.success("Appel mis à jour");
      } else {
        await api.post("/appels", payload);
        toast.success("Appel enregistré");
      }
      setOpen(false);
      setEditing(null);
      setForm({ type: "entrant", motif: "information", numero: "", duree_sec: 0, client_id: "", objet: "", resume: "", notes: "", genere_reclamation: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      type: r.type || "entrant",
      motif: r.motif || "information",
      numero: r.numero || "",
      duree_sec: r.duree_sec || 0,
      client_id: r.client_id || "",
      objet: r.objet || "",
      resume: r.resume || "",
      notes: r.notes || "",
      genere_reclamation: false,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet appel ?")) return;
    try {
      await api.delete(`/appels/${id}`);
      toast.success("Appel supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const displayed = q
    ? rows.filter((r) => r.numero?.toLowerCase().includes(q.toLowerCase()) || r.notes?.toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div>
      <PageHeader
        eyebrow="Module"
        title="Appels téléphoniques"
        description="Journal des appels entrants, sortants et manqués — avec génération de réclamation."
        testid="appels-header"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="appel-create-btn" className="bg-blue-900 hover:bg-blue-800 rounded-full">
                <Plus className="w-4 h-4 mr-2" /> Enregistrer un appel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Modifier l'appel" : "Nouvel appel"}</DialogTitle>
                <DialogDescription>Renseignez les informations sur l&apos;appel.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="appel-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Motif</Label>
                    <Select value={form.motif} onValueChange={(v) => setForm({ ...form, motif: v })}>
                      <SelectTrigger data-testid="appel-motif-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{MOTIFS.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Numéro</Label>
                    <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="appel-numero-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Durée (secondes)</Label>
                    <Input type="number" min={0} value={form.duree_sec} onChange={(e) => setForm({ ...form, duree_sec: e.target.value })} data-testid="appel-duree-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Client (optionnel)</Label>
                  <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="appel-client-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom} {c.prenom || ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Objet de l&apos;appel</Label>
                  <Input value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} data-testid="appel-objet-input" placeholder="Motif court" />
                </div>
                <div className="space-y-1.5">
                  <Label>Résumé (compte rendu)</Label>
                  <Textarea rows={2} value={form.resume} onChange={(e) => setForm({ ...form, resume: e.target.value })} data-testid="appel-resume-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Notes internes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appel-notes-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="gen-rec"
                    checked={form.genere_reclamation}
                    onCheckedChange={(v) => setForm({ ...form, genere_reclamation: !!v })}
                    data-testid="appel-genere-rec-checkbox"
                  />
                  <Label htmlFor="gen-rec" className="cursor-pointer">Générer une réclamation depuis cet appel</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving} data-testid="appel-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <Input
              className="max-w-sm"
              placeholder="Rechercher par numéro / notes"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="appel-search-input"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-44" data-testid="appel-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead>Généré rec.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400"><PhoneCall className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun appel</TableCell></TableRow>
                ) : displayed.map((r) => {
                  const Icon = ICONS[r.type] || PhoneCall;
                  const tone = r.type === "manque" ? "text-red-600" : r.type === "entrant" ? "text-emerald-600" : "text-blue-700";
                  return (
                    <TableRow key={r.id} data-testid={`appel-row-${r.id}`}>
                      <TableCell><span className={`inline-flex items-center gap-2 text-sm capitalize ${tone}`}><Icon className="w-4 h-4" /> {r.type}</span></TableCell>
                      <TableCell><StatutBadge value={r.statut || "termine"} /></TableCell>
                      <TableCell className="text-slate-700 text-sm capitalize">{r.motif?.replace("_", " ")}</TableCell>
                      <TableCell className="font-mono text-xs">{r.numero || "—"}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{r.duree_sec}s</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-700">{r.objet || r.resume || r.notes || "—"}</TableCell>
                      <TableCell>{r.reclamation_id ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Oui</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("fr-FR")}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid={`appel-edit-${r.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`appel-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
